import { useEffect, useState, useCallback } from "react";
import { getCalendarStatus, getCalendarEvents, getCalendarLoginUrl } from "../api";
import { CARD, todayISO } from "../lib/planner";
import { CalendarDays, Loader2, CalendarPlus } from "lucide-react";
import { Button } from "./ui/button";

const EventRow = ({ e }) => (
  <div
    data-testid={`calendar-event-${e.id}`}
    className="flex items-center gap-3 py-3 border-b border-stone-100 dark:border-stone-800 last:border-0"
  >
    <span className="w-16 shrink-0 text-xs font-semibold text-[#b1543b] dark:text-stone-300 tabular-nums">
      {e.all_day ? "All day" : e.start_time || "—"}
    </span>
    <div className="min-w-0">
      <p className="text-base text-[#3b2a24] dark:text-stone-100 truncate">{e.summary}</p>
      {!e.all_day && e.end_time && (
        <p className="text-xs text-stone-400 dark:text-stone-500">
          {e.start_time}–{e.end_time}
        </p>
      )}
    </div>
  </div>
);

export const TodaySchedule = () => {
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const st = await getCalendarStatus();
      setStatus(st);
      if (st.connected) {
        const res = await getCalendarEvents(todayISO());
        setEvents(res.events || []);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const connect = async () => {
    setConnecting(true);
    try {
      const { authorization_url } = await getCalendarLoginUrl();
      window.location.href = authorization_url;
    } catch {
      setConnecting(false);
    }
  };

  if (loading && !status) return null;
  if (!status?.configured) return null;

  if (!status.connected) {
    return (
      <div data-testid="calendar-connect-card" className={`${CARD} p-6 sm:p-8`}>
        <div className="flex items-center gap-2 mb-2">
          <CalendarDays className="h-5 w-5 text-stone-400" />
          <h2 className="font-heading text-2xl font-bold text-[#3b2a24] dark:text-stone-100">Today's schedule</h2>
        </div>
        <p className="text-sm text-stone-500 dark:text-stone-400 mb-5">
          Connect Google Calendar to see today's events and let Ebbly plan your tasks around your meetings.
        </p>
        <Button
          data-testid="connect-calendar-btn"
          onClick={connect}
          disabled={connecting}
          className="rounded-full bg-[#b1543b] hover:bg-[#8f3f2b] text-white gap-2"
        >
          <CalendarPlus className="h-4 w-4" />
          {connecting ? "Redirecting…" : "Connect Google Calendar"}
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="today-schedule-card" className={`${CARD} p-6 sm:p-8`}>
      <div className="flex items-center gap-2 mb-3">
        <CalendarDays className="h-5 w-5 text-stone-400" />
        <h2 className="font-heading text-2xl font-bold text-[#3b2a24] dark:text-stone-100">Today's schedule</h2>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-stone-400 py-4">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading your calendar…
        </div>
      ) : events.length ? (
        <div>{events.map((e) => <EventRow key={e.id} e={e} />)}</div>
      ) : (
        <p className="text-sm text-stone-400 py-2">No events today — lovely open space. 🌿</p>
      )}
    </div>
  );
};
